import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { api, API, errMsg, errDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useTryOnCredits } from "@/context/TryOnCreditsContext";
import { LockBadge } from "@/components/LockBadge";
import { ProductCard } from "@/components/ProductCard";
import { BundleBuilder } from "@/components/BundleBuilder";
import { CheckoutLightbox } from "@/components/CheckoutLightbox";
import { ReserveLightbox } from "@/components/ReserveLightbox";
import { SchoolVerifyModal } from "@/components/SchoolVerifyModal";
import { ShareLook } from "@/components/ShareLook";
import { TryOnWizard } from "@/components/TryOnWizard";
import { Seo, breadcrumbLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { productUrl, categoryUrl, brandUrl, categoryLabel, slugify } from "@/lib/productUrl";
import { toast } from "sonner";
import { Lock, Heart, Sparkles, Ruler, X, Loader2, ShoppingBag, Eye, Flame, Star } from "lucide-react";
import { MiniCountdown } from "@/components/MiniCountdown";
import { mpViewContent, mpAddToCart, mpAddToWishlist } from "@/lib/metaPixel";
import ExclusivityPanel from "@/components/product/ExclusivityPanel";
import SpecSheet from "@/components/product/SpecSheet";
import ProductReviews from "@/components/ProductReviews";
import { colorHex, splitCompoundColor } from "@/lib/colorMatch";

// --- Meta description helpers ------------------------------------------
// Meta descriptions should be unique, compelling summaries that highlight
// features, benefits, and value — NEVER restating the page title or product
// name. Target 150–160 chars, natural language, click-worthy.
const DRESS_KW = /\b(neckline|silhouette|sleeve|bodice|skirt|hemline|waistline|zipper|beading?|beaded|sequin|lace|satin|tulle|chiffon|jersey|mesh|organza|sheath|ballgown|a[-\s]?line|mermaid|trumpet|off[-\s]?shoulder|strapless|plunging|sweetheart|corset|illusion|applique|cutout|embroidered|embellish|floral|geometric|jewel|crystal|rhinestone|column|halter|v[-\s]?neck|scoop|high[-\s]?neck|open[-\s]?back|low[-\s]?back|slit|train|shimmer|glitter|sparkle|two[-\s]?piece|ballerina|fit[-\s]and[-\s]flare|beadwork|crystals|velvet|silk|jacket|lapel|shawl|peak|tuxedo|suit|patent|leather|knit|shoe)\b/i;

// Generic sales openers with no product-specific value — skip these entirely.
const GENERIC_OPENER = /^(step into|make your entrance|from photos|this look is|this standout|bring boutique|this is the kind|for prom|make a striking|elevate your formal|elevate your|shine with|leave a trail|channel|poise meets|welcome to|it also reveals)/i;

// Detects sentences that restate the brand/product identifier at the start
// (e.g. "The Amarra 89067 …", "Amarra 89067 is …", "This Amarra 89067 gown …",
// or "The Black Peak Velvet Tuxedo Jacket …" when the product name is embedded).
function _restatesTitle(sentence, brand, productName) {
  const lo = sentence.toLowerCase();
  // Check brand-based restatement.
  if (brand) {
    const brandLo = brand.toLowerCase();
    const idx = lo.indexOf(brandLo);
    if (idx !== -1 && idx <= 30) {
      const before = lo.slice(0, idx).trim();
      if (before === "" || /^(the|this|a|our|meet|introducing|shop|discover)$/i.test(before)) return true;
    }
  }
  // Check product-name restatement — sentence opens with the full product name
  // (allowing an optional leading "The ", "This ", or "A "). Compare on the
  // first ~50 chars of the sentence after stripping the article prefix.
  if (productName) {
    const nameLo = productName.toLowerCase().replace(/[\u2010-\u2015\u2212\-]/g, " ").replace(/\s+/g, " ").trim();
    if (nameLo.length >= 12) {
      const stripped = lo.replace(/^(the|this|a|our)\s+/i, "");
      if (stripped.startsWith(nameLo.slice(0, Math.min(nameLo.length, 45)))) return true;
    }
  }
  return false;
}

function _cleanDesc(raw, brand, productName, budget = 155) {
  if (!raw) return "";
  let s = String(raw)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const cutMarkers = [
    /Style:\s*[a-z0-9_-]+/i,
    /Please refer to our returns/i,
    /Please refer to A Size/i,      // catches unpunctuated Amoris-style boilerplate
    /Available through Heather/i,
    /returns? and exchanges? policy/i,
    /Details:\s*[A-Z]/,
    /Models are wearing/i,
    /Model is wearing/i,
    /Fabric:\s*[A-Z]/,
  ];
  for (const rx of cutMarkers) {
    const m = s.match(rx);
    if (m && m.index != null) s = s.slice(0, m.index).trim();
  }
  const sentences = (s.match(/[^.!?]+[.!?]+/g) || [s]).map((x) => x.trim()).filter(Boolean);

  // Filter out generic openers, title-restatements, and boilerplate references.
  const isBoilerplate = (x) => /please refer|size chart|returns? policy/i.test(x);
  const useful = sentences.filter(
    (x) => !GENERIC_OPENER.test(x) && !_restatesTitle(x, brand, productName) && !isBoilerplate(x)
  );
  const dressy = useful.filter((x) => DRESS_KW.test(x));

  const pack = (pool) => {
    const acc = [];
    let len = 0;
    for (const sent of pool) {
      const extra = (acc.length ? 1 : 0) + sent.length;
      if (len + extra <= budget) { acc.push(sent); len += extra; }
    }
    return acc.join(" ");
  };

  const dressPacked = pack(dressy);
  if (dressPacked) return dressPacked;

  const usefulPacked = pack(useful);
  if (usefulPacked) return usefulPacked;

  const anyPacked = pack(sentences);
  if (anyPacked) return anyPacked;

  const pool = useful.length ? useful : sentences;
  const shortest = [...pool].sort((a, b) => a.length - b.length)[0];
  if (!shortest) return "";
  if (shortest.length <= budget) return shortest;

  const cut = shortest.slice(0, budget);
  const sp = cut.lastIndexOf(" ");
  let stem = (sp > budget * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s.,;:—-]+$/g, "");
  const filler = /\s+(a|an|the|to|of|in|at|on|with|and|or|that|but|for|from|by|as|is|are|was|were|be|been|it|this|its|his|her|their|our|my|your|you)$/i;
  while (filler.test(stem)) stem = stem.replace(filler, "");
  return /[.!?]$/.test(stem) ? stem : `${stem}.`;
}

// --- Meta title/description helpers -----------------------------------------
// Product copy is inconsistent about separators (e.g. "Amarra 89067 - Strapless
// Geometric Beaded Prom Dress"). We strip every dash flavour and collapse the
// resulting whitespace so titles read as plain phrases in the SERP.
function _stripDashes(s) {
  return String(s || "")
    .replace(/[\u2010-\u2015\u2212\-]/g, " ")   // hyphen, en/em/figure/quotation dashes, minus, ASCII "-"
    .replace(/\s+/g, " ")
    .trim();
}

// Extract the alphanumeric model/style number from a product. Prefers SKU
// (usually already normalized) then falls back to parsing 4-6 digit tokens
// from the product name.
function _trimAtWord(s, max) {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  const stem = sp > max * 0.55 ? cut.slice(0, sp) : cut;
  return stem.replace(/[\s,\-–—]+$/g, "");
}

function _extractModel(product) {
  const sku = String(product.sku || "").trim();
  if (sku) {
    const m = sku.match(/\d{3,7}/);
    if (m) return m[0];
  }
  const nm = String(product.name || "");
  const m = nm.match(/\b(\d{4,6})\b/);
  return m ? m[1] : null;
}

function _titleCase(s) {
  return String(s || "")
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ")
    .trim();
}

function _occasionWord(product, cslug) {
  if (cslug === "prom-dresses") return "Prom";
  if (cslug === "homecoming-dresses") return "Homecoming";
  if (cslug === "graduation-dresses") return "Graduation";
  const occ = (product.occasion || "").toLowerCase();
  if (occ.includes("prom")) return "Prom";
  if (occ.includes("home")) return "Homecoming";
  if (occ.includes("grad")) return "Graduation";
  return "";
}

function _catNoun(cslug) {
  if (cslug === "tuxedos") return "Tuxedo";
  if (cslug === "shoes") return "Formal Shoes";
  if (cslug === "accessories") return "Accessory";
  return "Dress"; // dresses default
}

// Compose the SERP <title> with the format the boutique owns:
//     {Color} {Designer} {Silhouette} {Occasion} {CategoryNoun} {Model#}
//   e.g. "Gold Amarra Sheath Prom Dress 89059"
//        "Periwinkle/Silver Amarra Sheath Prom Dress 89112"
// Gracefully skips any part that isn't populated.
function _composeSmartTitle(product, cslug, activeColor, max = 68) {
  const brand = product.designer;
  const silhouette = product.silhouette ? _titleCase(product.silhouette) : "";
  const occasion = _occasionWord(product, cslug);
  const noun = _catNoun(cslug);
  const model = _extractModel(product);

  // Full ideal composition
  const idealParts = [activeColor, brand, silhouette, occasion, noun, model].filter(Boolean);
  const ideal = idealParts.join(" ");
  if (ideal.length <= max) return ideal;

  // Progressive fallback: drop optional pieces from least- to most-important.
  const droppable = [silhouette, occasion, model];
  for (let i = 0; i < droppable.length; i++) {
    droppable[i] = null;
    const parts = [activeColor, brand, droppable[0], droppable[1], noun, droppable[2]].filter(Boolean);
    const t = parts.join(" ");
    if (t.length <= max) return t;
  }

  // Last resort — just color + brand + noun
  const minimal = [activeColor, brand, noun].filter(Boolean).join(" ");
  return minimal.length <= max ? minimal : _trimAtWord(minimal, max);
}

// Compose meta description. Every color URL gets a UNIQUE description with
// color + spec + brand mention. Stays under `max` chars. Prefers an AI-
// generated per-color hook (product.variant_hooks[color]) when present.
function _composeSmartDesc(product, cslug, activeColor, max = 155) {
  // 1) AI-generated per-color hook wins if it fits the budget.
  const hooks = product.variant_hooks || {};
  const aiHook = activeColor && (hooks[activeColor] || hooks[String(activeColor).trim()]);
  if (aiHook && String(aiHook).trim().length <= max) {
    return String(aiHook).trim();
  }

  const brand = product.designer;
  const silhouette = product.silhouette ? String(product.silhouette).toLowerCase() : "";
  const neckline = product.neckline ? String(product.neckline).toLowerCase() + " neckline" : "";
  const noun = _catNoun(cslug).toLowerCase();
  const occasion = _occasionWord(product, cslug).toLowerCase();
  const model = _extractModel(product);

  // Head: "{Color} {Brand} {silhouette} {occasion} {noun} #{model}." (all optional)
  const headParts = [activeColor, brand].filter(Boolean);
  const bodyBits = [];
  if (silhouette) bodyBits.push(silhouette);
  if (occasion && noun) bodyBits.push(`${occasion} ${noun}`);
  else if (noun && cslug !== "shoes" && cslug !== "accessories") bodyBits.push(noun);
  const bodyStr = bodyBits.join(" ");
  const head = [headParts.join(" "), bodyStr].filter(Boolean).join(" ");
  const headWithModel = model ? `${head} #${model}` : head;

  // Tail: specs + exclusivity hook (branded)
  const specBits = [];
  if (silhouette && !bodyStr.includes(silhouette)) specBits.push(silhouette);
  if (neckline) specBits.push(neckline);
  const specStr = specBits.slice(0, 2).join(", ");
  const hook = "School-exclusive lock at Heather's Boutique — one dress per WI school.";

  const candidates = [
    [headWithModel, specStr, hook].filter(Boolean).join(" · "),
    [headWithModel, hook].filter(Boolean).join(" · "),
    [head, hook].filter(Boolean).join(" · "),
    hook,
  ];
  for (const c of candidates) {
    const t = c.replace(/\s+·\s+/g, " · ").trim();
    if (t.length <= max && t.length > 0) return t;
  }
  // If everything is over budget (unlikely), trim the last candidate.
  return _trimAtWord(candidates[candidates.length - 1], max);
}


export default function ProductDetail() {
  // Supports BOTH URL styles:
  //   Legacy: /product/:id
  //   Silo:   /:categorySlug/:productSlug[/:colorSlug]
  const location = useLocation();
  const params = useParams();
  const { id: legacyId, productSlug, colorSlug } = params;
  // Category slug is a static segment in the route — read from pathname.
  const categorySlug = legacyId ? null : location.pathname.split("/")[1];
  const { user } = useAuth();
  const tryOnCredits = useTryOnCredits();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [lock, setLock] = useState(null);
  const [size, setSize] = useState("");
  const [isFav, setIsFav] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [replacePrompt, setReplacePrompt] = useState(null);
  const [tryOnUrl, setTryOnUrl] = useState(null);
  const [showTryOn, setShowTryOn] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [creditPack, setCreditPack] = useState(null);
  const [schoolVerifyOpen, setSchoolVerifyOpen] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [colorIdx, setColorIdx] = useState(0);
  const [activeFit, setActiveFit] = useState(null);
  const [looks, setLooks] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviewsList, setReviewsList] = useState([]);

  const load = () => {
    const fetcher = legacyId
      ? api.product(legacyId)
      : api.productBySlug(categorySlug, productSlug);
    fetcher
      .then(({ data }) => {
        setProduct(data);
        setLock(data.lock);
        // If accessed via legacy /product/:id, redirect to canonical silo URL (301-equivalent via replace).
        if (legacyId && data.url) {
          navigate(data.url, { replace: true });
        }
        // If color is in URL, preselect it
        let preIdx = 0;
        if (colorSlug && data.variants?.length) {
          const idx = data.variants.findIndex((v) => slugify(v.color) === colorSlug);
          if (idx >= 0) { setColorIdx(idx); preIdx = idx; }
        }
        // Meta Pixel — ViewContent
        try { mpViewContent(data, data.variants?.[preIdx]?.color); } catch { /* noop */ }
      })
      .catch(() => { navigate("/shop", { replace: true }); });
    const idForSideCalls = legacyId; // side-calls only fire when we already know the id
    if (idForSideCalls) {
      api.productLooks(idForSideCalls).then(({ data }) => setLooks(data)).catch(() => {});
      if (user) {
        api.favorites().then(({ data }) => setIsFav(data.some((p) => p.id === idForSideCalls)));
        api.tryOnLatest(idForSideCalls).then(({ data }) => { if (data.result_url) { setTryOnUrl(`${process.env.REACT_APP_BACKEND_URL}${data.result_url}`); setShowTryOn(true); } }).catch(() => {});
        if (user.school_id) api.school(user.school_id).then(({ data }) => setSchoolName(data.name)).catch(() => {});
      }
    }
  };
  useEffect(load, [legacyId, categorySlug, productSlug, user]);

  // Once we know the product, fetch product-scoped side info (works for silo route too)
  useEffect(() => {
    if (!product?.id) return;
    api.productLooks(product.id).then(({ data }) => setLooks(data)).catch(() => {});
    if (user) {
      api.favorites().then(({ data }) => setIsFav(data.some((p) => p.id === product.id)));
      api.tryOnLatest(product.id).then(({ data }) => { if (data.result_url) { setTryOnUrl(`${process.env.REACT_APP_BACKEND_URL}${data.result_url}`); setShowTryOn(true); } }).catch(() => {});
      if (user.school_id) api.school(user.school_id).then(({ data }) => setSchoolName(data.name)).catch(() => {});
    }
  }, [product?.id, user]);

  useEffect(() => {
    if (!product) return;
    // If ?tryon=1 in URL, auto-open the try-on modal once (used by "Try This Look" 1-click from cards)
    const params = new URLSearchParams(location.search);
    if (params.get("tryon") === "1") {
      if (user) setTryOnOpen(true);
      // clean the query so navigating back doesn't re-trigger
      navigate(location.pathname, { replace: true });
    }
  }, [product?.id]); // eslint-disable-line

  useEffect(() => {
    if (!product) return;
    const vs = product.variants?.length ? product.variants : null;
    const c = vs ? vs[Math.min(colorIdx, vs.length - 1)]?.color : null;
    if (user) {
      api.productLock(product.id, c).then(({ data }) => setLock(data.lock)).catch(() => {});
    }
    // Sync selected color into the URL (shareable + indexable) via replaceState — works for all users.
    if (vs && c && product.category_slug && product.slug) {
      const canonical = productUrl(product, c);
      if (window.location.pathname !== canonical) {
        window.history.replaceState(null, "", canonical);
      }
    }
  }, [colorIdx, product, user]);

  if (!product) return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  const id = product.id;
  const mine = lock && user && lock.user_id === user.id;
  const lockedByOther = lock && lock.status === "active" && !mine;
  const purchased = lock && lock.status === "purchased";

  const variants = product.variants?.length ? product.variants : null;
  const activeVariant = variants ? variants[Math.min(colorIdx, variants.length - 1)] : null;
  const dressImg = activeVariant?.image || product.images[0];
  const activeFitObj = product.fits?.find((f) => f.fit === activeFit) || (product.fits?.length === 1 ? product.fits[0] : null);
  const sizesList = activeFitObj?.sizes?.length ? activeFitObj.sizes : (activeVariant?.sizes?.length ? activeVariant.sizes : product.sizes);
  const displayProduct = activeVariant ? { ...product, images: [dressImg], color: activeVariant.color } : product;
  // AI Try-On is dress-only. Hide the button + auto-open on tuxedos/accessories/shoes.
  // Also hide for parent accounts — try-on is student-only (their face, their lookbook).
  const tryOnEligible = !["tuxedos", "accessories", "shoes"].includes(product.category_slug) && user?.role !== "parent";

  const requireAuth = () => { if (!user) { navigate("/auth"); return false; } return true; };

  const canonicalPath = productUrl(product, activeVariant?.color || null);
  const cslug = product.category_slug || "prom-dresses";
  const bslug = product.brand_slug || slugify(product.designer || "atelier");
  const activeColor = activeVariant?.color || product.color;
  // Breadcrumbs: brand deliberately omitted — brand is not in the product URL path.
  const crumbs = [
    { name: "Home", path: "/" },
    { name: categoryLabel(cslug), path: categoryUrl(cslug) },
    { name: product.name, path: productUrl(product) },
  ];
  // --- Meta title & description ---------------------------------------
  // Title: plain-text "Brand ProductName" (no dashes, no ellipsis, ≤60 chars).
  // If the product.name already starts with the brand we don't double-prepend.
  // Colour is appended with a comma when it fits, dropped otherwise.
  const brand = (product.designer || "").trim();
  const cleanName = _stripDashes(product.name);
  const seoTitle = _composeSmartTitle(product, cslug, activeColor, 68);
  const seoDesc = _composeSmartDesc(product, cslug, activeColor, 155);

  const productLd = (() => {
    const props = [];
    if (product.silhouette) props.push({ "@type": "PropertyValue", name: "Silhouette", value: product.silhouette });
    if (product.neckline) props.push({ "@type": "PropertyValue", name: "Neckline", value: product.neckline });
    if (product.length) props.push({ "@type": "PropertyValue", name: "Length", value: product.length });
    if (product.waistline) props.push({ "@type": "PropertyValue", name: "Waistline", value: product.waistline });
    if (product.occasion) props.push({ "@type": "PropertyValue", name: "Occasion", value: product.occasion });
    const material = product.fabric || product.material;

    // Per-color variant array — helps Google surface all colors in Shopping.
    // We synthesize an Offer per variant (same price, same availability policy).
    const buildOffer = (color) => ({
      "@type": "Offer",
      price: product.price,
      priceCurrency: "USD",
      availability: purchased ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      url: color ? `${ORIGIN}${canonicalPath.replace(/\/[^/]*$/, "")}/${slugify(color)}` : `${ORIGIN}${canonicalPath}`,
      itemOffered: color ? { "@type": "Product", name: `${product.name} — ${color}`, color } : undefined,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Heather's Boutique", url: ORIGIN },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 14,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "USD" },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "US", addressRegion: "WI" },
        deliveryTime: { "@type": "ShippingDeliveryTime", handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" }, transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" } },
      },
    });

    // Build the primary Offer(s) — if multi-color, use AggregateOffer so Google
    // shows the color range in Shopping listings.
    const variantColors = (product.variants || []).map((v) => v.color).filter(Boolean);
    const offers = variantColors.length > 1
      ? {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: product.price,
          highPrice: product.price,
          offerCount: variantColors.length,
          offers: variantColors.map(buildOffer),
        }
      : buildOffer(activeColor);

    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: activeColor ? `${product.name} — ${activeColor}` : product.name,
      description: seoDesc,
      image: [dressImg, ...product.images.filter((i) => i !== dressImg)],
      color: activeColor,
      sku: product.sku ? `${product.sku}${activeColor ? "-" + slugify(activeColor).toUpperCase() : ""}` : undefined,
      mpn: product.sku || undefined,
      productID: product.id,
      category: categoryLabel(cslug),
      material: material || undefined,
      itemCondition: "https://schema.org/NewCondition",
      brand: {
        "@type": "Brand",
        name: product.designer || "Heather's Boutique",
        url: `${ORIGIN}${brandUrl(bslug)}`,
      },
      offers,
    };
    if (props.length) ld.additionalProperty = props;

    // isVariantOf → ProductGroup pattern helps Google understand this is one
    // dress in multiple colors (better than distinct products).
    if (variantColors.length > 1) {
      ld.isVariantOf = {
        "@type": "ProductGroup",
        productGroupID: product.id,
        name: product.name,
        variesBy: ["color"],
        hasVariant: variantColors.map((c) => ({
          "@type": "Product",
          name: `${product.name} — ${c}`,
          color: c,
          url: `${ORIGIN}${canonicalPath.replace(/\/[^/]*$/, "")}/${slugify(c)}`,
        })),
      };
    }

    if (reviewSummary && reviewSummary.count > 0) {
      ld.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: reviewSummary.average,
        reviewCount: reviewSummary.count,
        bestRating: 5,
        worstRating: 1,
      };
    }

    // Include up to 5 recent visible reviews so Google can render individual
    // review snippets in the SERP.
    if (Array.isArray(reviewsList) && reviewsList.length > 0) {
      ld.review = reviewsList.slice(0, 5).map((r) => ({
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
        author: { "@type": "Person", name: r.user_name || "Anonymous" },
        datePublished: r.created_at,
        name: r.title,
        reviewBody: r.body,
      }));
    }

    return ld;
  })();

  const addToCart = async () => {
    // No auth required — guests can add to cart. CartContext stores the
    // items in localStorage and merges them into the server cart when the
    // shopper signs in (typically at the checkout step).
    if (product.category !== "accessories" && !size) { toast("Please select a size"); return; }
    await addItem({ product_id: id, size, quantity: 1, color: activeVariant?.color || null });
    // Meta Pixel — AddToCart
    try { mpAddToCart({ product, color: activeVariant?.color || null, size, quantity: 1 }); } catch { /* noop */ }
  };

  const doLock = async (replace = false) => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const { data } = await api.createLock(id, replace, activeVariant?.color || null);
      setLock(data);
      setReplacePrompt(null);
      toast.success("Locked — held just for you. Secure it before the timer runs out!");
    } catch (e) {
      const detail = errDetail(e);
      if (detail && detail.code === "existing_lock") {
        setReplacePrompt(detail.current_lock);
      } else {
        toast.error(errMsg(e));
      }
    } finally { setBusy(false); }
  };

  const secureForever = async () => {
    if (!requireAuth()) return;
    setReserveOpen(true);
  };

  const PACK_INFO = { "1": { count: 1, price: 1.99 }, "15": { count: 15, price: 9.99 }, "35": { count: 35, price: 19.99 } };
  const openCreditCheckout = (pack) => {
    // Remember which product page we're on so /checkout/return can send us
    // straight back to this dress after a successful credit purchase.
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("hb_last_tryon_product_url", window.location.pathname);
      }
    } catch { /* ignore */ }
    setTryOnOpen(false);
    setCreditPack(pack);
  };

  const toggleFav = async () => {
    if (!requireAuth()) return;
    const { data } = await api.toggleFavorite(id);
    setIsFav(data.added);
    // Meta Pixel — AddToWishlist (only on ADD, never on remove)
    if (data.added) {
      try { mpAddToWishlist(product, activeVariant?.color || null); } catch { /* noop */ }
    }
  };

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={canonicalPath}
        image={dressImg || product.images[0]}
        jsonLd={[productLd, breadcrumbLd(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
        {/* image */}
        <div className="relative md:sticky md:top-24 md:self-start">
          {lock && product.lockable !== false && <LockBadge lock={lock} schoolName={user?.school_id} mine={mine} floating />}
          <div className="aspect-[3/4] overflow-hidden bg-blush rounded-sm">
            <img src={showTryOn && tryOnUrl ? tryOnUrl : dressImg} alt={product.name} className="w-full h-full object-cover object-top transition-opacity duration-500" />
          </div>
          {tryOnUrl && tryOnEligible && (
            <div className="mt-3 flex justify-center" data-testid="tryon-toggle-wrap">
              <div className="glass-dark rounded-full p-1 inline-flex gap-1" data-testid="tryon-toggle">
                <button onClick={() => setShowTryOn(false)} className={`font-label text-[9px] px-4 py-1.5 rounded-full transition-colors ${!showTryOn ? "bg-champagne text-ink" : "text-ivory"}`}>The Dress</button>
                <button onClick={() => setShowTryOn(true)} className={`font-label text-[9px] px-4 py-1.5 rounded-full transition-colors ${showTryOn ? "bg-champagne text-ink" : "text-ivory"}`}>You In It ✨</button>
              </div>
            </div>
          )}
        </div>

        {/* info */}
        <div className="md:pt-6">
          {/* Brand row: logo (or wordmark) linked to the brand hub */}
          {product.designer && (
            <Link to={brandUrl(bslug)} data-testid="brand-link" className="inline-flex items-center gap-3 mb-4 group">
              {product.brand_logo_url ? (
                <img
                  src={product.brand_logo_url}
                  alt={product.designer}
                  className="h-8 w-auto max-w-[140px] object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                />
              ) : (
                <span className="font-display text-2xl leading-none group-hover:text-rosemetal transition-colors">{product.designer}</span>
              )}
              <span className="font-label text-[9px] text-rosemetal tracking-widest group-hover:underline underline-offset-4">SHOP {product.designer.toUpperCase()} →</span>
            </Link>
          )}
          <p className="font-label text-[10px] text-rosemetal mb-3">{activeVariant ? activeVariant.color : product.color} · {product.occasion}</p>
          <h1 data-testid="pdp-h1" className="font-display text-5xl sm:text-6xl leading-[1.02]">{seoTitle}</h1>
          {/* Preserve the descriptor language (e.g. "Linear Beaded", "Two-Piece Embellished") as a small subtitle so we don't lose the marketing richness when the H1 collapses to the SEO-tight format. */}
          {(() => {
            const smart = seoTitle.toLowerCase();
            const stripped = _stripDashes(product.name);
            // Extract only the words in the full name that aren't already in the SEO title
            const richBits = stripped
              .split(/\s+/)
              .filter((w) => w && !smart.includes(w.toLowerCase()) && !/^\d+$/.test(w));
            const descriptor = richBits.join(" ").trim();
            return descriptor ? (
              <p data-testid="pdp-h1-descriptor" className="font-display italic text-lg text-ink/50 mt-3">
                {descriptor}
              </p>
            ) : null;
          })()}
          {reviewSummary && reviewSummary.count > 0 && (
            <a
              href="#reviews"
              data-testid="pdp-rating-anchor"
              onClick={(e) => { e.preventDefault(); document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
              className="inline-flex items-center gap-2 mt-3 group"
            >
              <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-4 h-4 ${reviewSummary.average >= s ? "fill-champagne text-champagne" : reviewSummary.average >= s - 0.5 ? "fill-champagne/50 text-champagne" : "text-ink/20"}`}
                    strokeWidth={1.5}
                  />
                ))}
              </span>
              <span className="font-label text-[10px] tracking-widest text-ink/60 group-hover:text-rosemetal transition-colors">
                {reviewSummary.average.toFixed(1)} · {reviewSummary.count} REVIEW{reviewSummary.count !== 1 ? "S" : ""}
              </span>
            </a>
          )}
          <p className="font-body text-2xl mt-4">${product.price}</p>
          <p data-testid="klarna-financing-line" className="font-body text-xs text-muted-foreground mt-1.5">or 4 interest-free payments of <b className="text-ink">${(product.price / 4).toFixed(2)}</b> with <b className="text-[#FFA8CD] bg-ink px-1.5 py-0.5 rounded-sm text-[10px]">Klarna</b> · Apple Pay & Google Pay at checkout</p>
          {(product.interest?.viewing > 0 || product.interest?.saved > 0) && (
            <div className="flex items-center gap-3 mt-4" data-testid="social-proof">
              {product.interest.viewing > 0 && (
                <span className="inline-flex items-center gap-1.5 font-label text-[9px] text-rosemetal bg-blush px-2.5 py-1 rounded-full">
                  <Eye className="w-3 h-3" /> {product.interest.viewing} {product.interest.viewing === 1 ? "student is" : "students are"} eyeing this at your school
                </span>
              )}
              {product.interest.saved > 0 && (
                <span className="inline-flex items-center gap-1.5 font-label text-[9px] text-ink bg-champagne/50 px-2.5 py-1 rounded-full">
                  <Flame className="w-3 h-3" /> {product.interest.saved} saved it
                </span>
              )}
            </div>
          )}
          {(() => {
            // Prefer the per-variant description that was written from the
            // approved xlsx import (`activeVariant.short_description`) so
            // the shopper sees copy scoped to the color they're viewing.
            // Fall back to the product-level short (mirrored from rank-1),
            // then the legacy variant_hook / long-description excerpt.
            const variantShort = String(activeVariant?.short_description || "").trim();
            const hooks = product.variant_hooks || {};
            const colorHook = activeColor && (hooks[activeColor] || hooks[String(activeColor).trim()]);
            const ai = variantShort || String(product.short_description || colorHook || "").trim();
            if (ai) {
              return <p data-testid="pdp-short-desc" className="font-body text-sm text-ink/70 mt-6 leading-relaxed max-w-md">{ai}</p>;
            }
            const cleaned = _cleanDesc(product.description || "", brand, cleanName, 240);
            const excerpt = cleaned || product.description || "";
            const short = excerpt.length > 220 ? excerpt.slice(0, 220).replace(/\s+\S*$/, "") + "…" : excerpt;
            return short ? (
              <p data-testid="pdp-short-desc" className="font-body text-sm text-ink/70 mt-6 leading-relaxed max-w-md">{short}</p>
            ) : null;
          })()}

          {/* colors */}
          {variants && variants.length > 1 && (() => {
            // If every variant points at the same image (a DB data-quality
            // issue for ~12% of products), swatch photos would repeat 6× and
            // mislead the shopper. Fall back to solid color chips so each
            // color is visually distinct and honest.
            const distinctImgs = new Set(variants.map((v) => v.image).filter(Boolean));
            const useSolidChips = distinctImgs.size <= 1;
            return (
              <div className="mt-8" data-testid="color-selector">
                <div className="font-label text-[9px] text-muted-foreground mb-3">Color · <span className="text-ink">{activeVariant.color}</span></div>
                {useSolidChips ? (
                  <div className="flex flex-wrap gap-2">
                    {variants.map((v, i) => {
                      const parts = splitCompoundColor(v.color);
                      const hexes = parts.map(colorHex).filter(Boolean);
                      // Fallback to full string lookup if compound split yielded nothing.
                      if (hexes.length === 0) {
                        const h = colorHex(v.color);
                        if (h) hexes.push(h);
                      }
                      // Ultimate fallback — neutral gray with a red dot to signal "unmapped".
                      if (hexes.length === 0) hexes.push("#D0D0D0");
                      const gradient = hexes.length === 1
                        ? hexes[0]
                        : `linear-gradient(135deg, ${hexes.map((h, j) => `${h} ${(j*100/hexes.length).toFixed(0)}%, ${h} ${((j+1)*100/hexes.length).toFixed(0)}%`).join(", ")})`;
                      const active = i === colorIdx;
                      const light = ["#FAFAFA", "#F7F1E7", "#F7E7CE", "#F3D1D3", "#FFD1DC", "#FBC3D3", "#F6A3C3", "#C6E4CB", "#ADD8E6", "#87CEEB", "#C6B5E0", "#C8A2C8", "#B6C0E1", "#CBB0DF", "#E5E4E2", "#D2B48C", "#D8C6A0", "#E3BC9A", "#EFCFC9", "#E8B0B2", "#D89A9C", "#B4C4A5", "#ACE1AF", "#E6C670", "#B8E434", "#39FF14"].includes(hexes[0]);
                      return (
                        <button
                          key={v.color}
                          data-testid={`color-swatch-${i}`}
                          title={v.color}
                          onClick={() => { setColorIdx(i); setShowTryOn(false); setSize(""); }}
                          className={`group flex flex-col items-center gap-1.5 transition-all ${active ? "" : "opacity-70 hover:opacity-100"}`}
                        >
                          <span
                            className={`w-11 h-11 rounded-full border-2 transition-all ${active ? "border-ink shadow-md scale-110" : "border-border group-hover:border-ink/50"} ${light ? "ring-1 ring-inset ring-black/10" : ""}`}
                            style={hexes.length === 1 ? { backgroundColor: hexes[0] } : { background: gradient }}
                            aria-hidden
                          />
                          <span className={`font-label text-[9px] tracking-widest max-w-[68px] truncate ${active ? "text-ink" : "text-muted-foreground"}`}>{v.color}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2.5">
                    {variants.map((v, i) => (
                      <button
                        key={v.color}
                        data-testid={`color-swatch-${i}`}
                        title={v.color}
                        onClick={() => { setColorIdx(i); setShowTryOn(false); setSize(""); }}
                        className={`relative w-14 rounded-sm overflow-hidden border-2 transition-all ${i === colorIdx ? "border-ink shadow-md" : "border-border opacity-80 hover:opacity-100 hover:border-ink/40"}`}
                      >
                        <div className="aspect-[3/4] bg-blush"><img src={v.image} alt={v.color} className="w-full h-full object-cover object-top" /></div>
                        <span className={`block font-label text-[6.5px] px-0.5 py-1 truncate ${i === colorIdx ? "bg-ink text-ivory" : "bg-ivory text-muted-foreground"}`}>{v.color}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Fit selector (tuxedo jackets/pants with multiple fits) */}
          {product.fits?.length > 1 && (
            <div className="mt-6" data-testid="fit-selector">
              <div className="font-label text-[9px] text-muted-foreground mb-3">Select Fit</div>
              <div className="flex flex-wrap gap-2">
                {product.fits.map((f) => (
                  <button
                    key={f.fit}
                    data-testid={`fit-${f.fit.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => { setActiveFit(f.fit); setSize(""); }}
                    className={`px-4 py-2 font-body text-sm border rounded-sm transition-colors ${activeFit === f.fit ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}
                  >
                    {f.fit}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* sizes */}
          {sizesList?.length > 0 && (
            <div className="mt-8">
              <div className="font-label text-[9px] text-muted-foreground mb-3">Select Size{activeFit ? ` · ${activeFit}` : activeVariant ? ` · available in ${activeVariant.color}` : ""}</div>
              <div className="flex flex-wrap gap-2">
                {sizesList.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)} className={`min-w-[44px] px-3 py-2 font-body text-sm border rounded-sm transition-colors ${size === s ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {/* Exclusivity WOW panel — the money moment */}
          <ExclusivityPanel
            product={product}
            lock={lock}
            user={user}
            schoolName={schoolName}
            interest={product.interest}
            busy={busy}
            onLock={() => doLock()}
            onBump={() => setCheckout({ kind: "bump", title: "Bump this reservation", lines: [{ name: "Priority lock transfer", price: "$19.00" }], total: 19, note: "You'll take the lock the moment payment clears.", cta: "Pay $19 to Bump" })}
            onSecureForever={secureForever}
            onShareLook={() => setShowShare(true)}
            onCallToLogin={() => navigate("/auth")}
          />

          {/* actions */}
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              data-testid="add-to-cart-btn"
              onClick={addToCart}
              disabled={purchased || product.is_sold_out}
              className="flex-1 min-w-[180px] bg-ink text-ivory px-6 py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:hover:bg-ink"
            >
              {product.is_sold_out ? <>Sold Out</> : <><ShoppingBag className="w-4 h-4" /> Add To Bag</>}
            </button>
            <button data-testid="fav-btn" onClick={toggleFav} className="px-5 py-4 border border-border rounded-sm hover:border-ink transition-colors">
              <Heart className={`w-4 h-4 ${isFav ? "fill-rosemetal text-rosemetal" : ""}`} />
            </button>
          </div>
          {product.is_sold_out && (
            <p data-testid="pdp-sold-out-note" className="font-label text-[10px] tracking-widest text-rosemetal mt-2">
              THIS DRESS IS NO LONGER AVAILABLE · TRY A SIMILAR STYLE BELOW
            </p>
          )}

          {/* AI concierge moments */}
          <div className={`grid ${tryOnEligible ? "grid-cols-2" : "grid-cols-1"} gap-3 mt-4`}>
            <button data-testid="fit-guidance-btn" onClick={() => (requireAuth() && setFitOpen(true))} className="glass-dark text-ivory px-5 py-4 rounded-sm flex items-center gap-2 hover:scale-[1.02] transition-transform">
              <Ruler className="w-4 h-4 text-champagne" /> <span className="font-label text-[9px]">AI Fit Guidance</span>
            </button>
            {tryOnEligible && (
            <button data-testid="tryon-btn" onClick={() => (requireAuth() && setTryOnOpen(true))} className="glass-dark text-ivory px-5 py-4 rounded-sm flex items-center gap-2 hover:scale-[1.02] transition-transform">
              <Sparkles className="w-4 h-4 text-champagne" />
              <span className="font-label text-[9px]">Virtual Try-On</span>
              {user && !tryOnCredits.is_vip && (
                <span data-testid="tryon-btn-credits" className="ml-auto font-label text-[9px] text-champagne bg-ink/60 px-1.5 py-0.5 rounded-full">
                  {tryOnCredits.credits}
                </span>
              )}
              {user && tryOnCredits.is_vip && (
                <span data-testid="tryon-btn-credits" className="ml-auto font-label text-[9px] text-ink bg-champagne px-1.5 py-0.5 rounded-full">
                  VIP
                </span>
              )}
            </button>
            )}
          </div>

          {/* Full specification sheet + long description */}
          <SpecSheet product={product} activeVariant={activeVariant} />
        </div>
      </div>

      {/* Product Reviews & Ratings */}
      <ProductReviews product={product} activeColor={activeColor} onSummary={setReviewSummary} onReviewsLoaded={setReviewsList} />

      {/* Locked & Loved — real girls in this dress (consented AI try-ons) */}
      {looks.length > 0 && (
        <section data-testid="locked-looks-section" className="mt-28 relative bg-ink text-ivory rounded-sm overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 15% 25%, #F7E7CE 1px, transparent 1px), radial-gradient(circle at 75% 65%, #F7E7CE 1px, transparent 1px)", backgroundSize: "80px 80px, 120px 120px" }} />
          <div className="relative px-6 sm:px-12 py-14">
            <p className="font-label text-[10px] text-champagne mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Locked &amp; Loved</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">Real girls. This dress.<br /><span className="italic text-champagne">Already claimed it.</span></h2>
            <p className="font-body text-sm text-ivory/60 mt-3 mb-10 max-w-md">AI try-ons proudly shared by students who made this look theirs.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-4xl">
              {looks.map((l, i) => (
                <figure key={i} data-testid={`locked-look-${i}`} className="relative rounded-sm overflow-hidden group border border-ivory/10">
                  <div className="aspect-[3/4] bg-ink"><img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt={`${l.first_name}'s look`} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" /></div>
                  <span className="absolute top-3 left-3 glass-dark text-ivory font-label text-[8px] px-2.5 py-1 rounded-full flex items-center gap-1"><Lock className="w-2.5 h-2.5 text-champagne" /> Locked her look</span>
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink via-ink/75 to-transparent p-4 pt-12">
                    <p className="font-display text-2xl leading-none">{l.first_name}</p>
                    <p className="font-label text-[8px] text-champagne mt-1.5">{l.school_name || "Wisconsin"} · {l.event}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Complete the Look — matching tuxedo pieces + shoes */}
      {product.bundle?.length > 0 && (
        <section data-testid="bundle-section" className="mt-24">
          <BundleBuilder product={product} bundle={product.bundle} />
        </section>
      )}

      {/* recommendations */}
      {product.recommendations?.length > 0 && (
        <section className="mt-32">
          <h2 className="font-display text-4xl mb-8">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {product.recommendations.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {fitOpen && <FitModal product={product} onClose={() => setFitOpen(false)} />}
      {tryOnOpen && tryOnEligible && <TryOnWizard product={displayProduct} onBuyCredits={openCreditCheckout} onClose={() => { setTryOnOpen(false); load(); }} />}

      <ReserveLightbox
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        onSuccess={load}
        product={displayProduct}
        selectedColor={activeVariant?.color || null}
        tryOnUrl={tryOnUrl}
        lock={lock}
        firstName={(user?.name || "").split(" ")[0] || "there"}
        schoolName={schoolName}
        viewers={product.interest?.viewing || 0}
        onSchoolVerifyRequired={() => setSchoolVerifyOpen(true)}
      />

      <SchoolVerifyModal
        open={schoolVerifyOpen}
        reason="checkout"
        onClose={() => { setSchoolVerifyOpen(false); tryOnCredits.refresh(); load(); }}
      />

      <CheckoutLightbox
        open={!!creditPack}
        onClose={() => setCreditPack(null)}
        onSuccess={async () => {
          // Refresh header + PDP button counters immediately.
          try { await tryOnCredits.refresh(); } catch { /* ignore */ }
        }}
        onDone={() => {
          // After the user clicks "Done" on the credit-purchase success screen,
          // drop them straight back into the try-on flow for THIS dress —
          // don't shove them onto /try-on/ or an unrelated page.
          setCreditPack(null);
          setTryOnOpen(true);
        }}
        onSchoolVerifyRequired={() => { setCreditPack(null); setSchoolVerifyOpen(true); }}
        kind="credits"
        payload={{ pack: creditPack }}
        title="Virtual try-on credits"
        lines={creditPack ? [{ name: `${PACK_INFO[creditPack].count} AI try-on credits`, price: `$${PACK_INFO[creditPack].price}` }] : []}
        total={creditPack ? PACK_INFO[creditPack].price : 0}
        note="Credits never expire — style yourself in any dress, any time."
        cta="Continue to Secure Payment"
        successNote="Your credits are ready. Let's see you in this dress."
      />

      <CheckoutLightbox
        open={!!checkout}
        onClose={() => setCheckout(null)}
        onSuccess={load}
        kind={checkout?.kind}
        productId={id}
        title={checkout?.title}
        lines={checkout?.lines || []}
        total={checkout?.total}
        note={checkout?.note}
        cta={checkout?.cta}
      />

      <ShareLook
        productId={product.id}
        open={showShare}
        onClose={() => setShowShare(false)}
        imageUrl={showTryOn && tryOnUrl ? tryOnUrl : dressImg}
        productName={product.name}
        schoolName={schoolName}
        productUrl={`${ORIGIN}/locked/${product.id}${user?.school_id ? `?s=${user.school_id}` : ""}`}
        remainingLabel={lock ? (() => { const ms = Math.max(0, new Date(lock.expires_at) - Date.now()); return `${String(Math.floor(ms / 60000)).padStart(2, "0")}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`; })() : "15:00"}
      />

      {replacePrompt && (
        <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReplacePrompt(null)}>
          <div data-testid="replace-lock-modal" className="bg-ivory rounded-sm max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-3xl mb-3">Swap your lock?</h3>
            <p className="font-body text-sm text-muted-foreground mb-6">
              You can only hold <b>one item</b> at a time. You currently have the <b>{replacePrompt.product_name}</b> locked.
              Unlock it and lock the <b>{product.name}</b> instead?
            </p>
            <div className="flex gap-3">
              <button data-testid="confirm-replace-lock" onClick={() => doLock(true)} disabled={busy} className="flex-1 bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Unlock & Lock This
              </button>
              <button onClick={() => setReplacePrompt(null)} className="flex-1 border border-ink py-3.5 font-label text-[10px] rounded-sm">Keep {replacePrompt.product_name}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid={testid} className="bg-ivory rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto p-8 relative animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-5 right-5"><X className="w-5 h-5" /></button>
        {children}
      </div>
    </div>
  );
}

function FitModal({ product, onClose }) {
  const [form, setForm] = useState({ height: "", weight: "", bust: "", waist: "", hips: "", body_shape: "", preferences: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.height) { toast("Add at least your height"); return; }
    setBusy(true);
    try {
      const { data } = await api.fit({ ...form, product_id: product.id });
      setResult(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="fit-modal">
      <p className="font-label text-[9px] text-rosemetal mb-2">AI Fit Concierge</p>
      <h3 className="font-display text-3xl mb-6">Find your perfect fit</h3>
      {!result ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[["height", "Height (e.g. 5'6\")"], ["weight", "Weight (opt)"], ["bust", "Bust (opt)"], ["waist", "Waist (opt)"], ["hips", "Hips (opt)"], ["body_shape", "Body shape (opt)"]].map(([k, ph]) => (
              <input key={k} data-testid={`fit-${k}`} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-rosemetal" />
            ))}
          </div>
          <textarea data-testid="fit-preferences" placeholder="Preferences (coverage, comfort, statement…)" value={form.preferences} onChange={(e) => setForm({ ...form, preferences: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-rosemetal" rows={2} />
          <button data-testid="fit-submit" onClick={submit} disabled={busy} className="w-full bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Get My Fit Guidance
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="fit-result">
          <div className="bg-champagne/40 rounded-sm p-5">
            <div className="font-label text-[9px] text-rosemetal">Recommended Size</div>
            <div className="font-display text-4xl">{result.recommended_size}</div>
          </div>
          <p className="font-body text-sm"><b>Silhouette:</b> {result.silhouette}</p>
          <p className="font-body text-sm italic text-rosemetal">{result.confidence_note}</p>
          <ul className="space-y-2">{(result.tips || []).map((t, i) => <li key={i} className="font-body text-sm text-muted-foreground">— {t}</li>)}</ul>
          <button onClick={onClose} className="w-full border border-ink py-3 font-label text-[10px] rounded-sm">Done</button>
        </div>
      )}
    </Modal>
  );
}

